'use client';

import { useEffect, useState } from 'react';

import { Bell, X } from 'lucide-react';

import { useUser } from '@kit/supabase/hooks/use-user';

import { usePushNotifications } from '~/lib/hooks/use-push-notifications';
import { safeLocalStorage } from '~/lib/safe-storage';

const DISMISS_KEY = 'push-prompt-dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SHOW_DELAY_MS = 5000; // 5 seconds after page load
// Hot-moment nudge (fired by the chat page right after the user's FIRST
// character reply — peak emotional buy-in). Shown at most once ever.
export const PUSH_NUDGE_EVENT = 'edem:push-nudge';
const HOT_NUDGE_SHOWN_KEY = 'push-prompt-hot-shown';

export function PushNotificationPrompt() {
  // Logged-out visitors (all marketing traffic) can never see this prompt —
  // gate BEFORE the push hook so subscription/support checks and the nudge
  // listener don't run for them.
  const { data: user } = useUser();

  if (!user) return null;

  return <PushNotificationPromptInner />;
}

function PushNotificationPromptInner() {
  const [showBanner, setShowBanner] = useState(false);
  // Set when the banner was triggered by the hot-moment event — personalizes
  // the copy with the character's name.
  const [hotCharacter, setHotCharacter] = useState<string | null>(null);
  const { isSupported, isSubscribed, subscribe } = usePushNotifications();
  const { data: user } = useUser();

  // Hot-moment trigger: the chat page dispatches PUSH_NUDGE_EVENT after the
  // first reply. Bypasses the generic 5s delay and the 7-day snooze (it fires
  // once ever), but still respects browser permission + existing subscription.
  useEffect(() => {
    const onNudge = (e: Event) => {
      if (!user || !isSupported || isSubscribed) return;
      if (
        typeof Notification !== 'undefined' &&
        Notification.permission !== 'default'
      )
        return;
      if (safeLocalStorage.getItem(HOT_NUDGE_SHOWN_KEY)) return;

      safeLocalStorage.setItem(HOT_NUDGE_SHOWN_KEY, String(Date.now()));
      const detail = (e as CustomEvent<{ character?: string }>).detail;
      setHotCharacter(detail?.character ?? null);
      setShowBanner(true);
    };

    window.addEventListener(PUSH_NUDGE_EVENT, onNudge);
    return () => window.removeEventListener(PUSH_NUDGE_EVENT, onNudge);
  }, [user, isSupported, isSubscribed]);

  useEffect(() => {
    // Don't show if not logged in
    if (!user) return;

    // Don't show if push not supported or already subscribed
    if (!isSupported || isSubscribed) return;

    // Don't show if browser already granted or denied (only prompt on "default")
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission !== 'default'
    )
      return;

    // Don't show if dismissed recently
    const dismissedAt = safeLocalStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_DURATION_MS)
      return;

    const timer = setTimeout(() => {
      // Check if PWA install banner is currently in the DOM
      const pwaInstallBanner = document.querySelector('[data-pwa-install]');
      if (pwaInstallBanner) return;

      setShowBanner(true);
    }, SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isSupported, isSubscribed, user]);

  const handleEnable = async () => {
    const success = await subscribe();
    setShowBanner(false);
    if (!success) {
      // User denied — don't nag them again for 7 days
      safeLocalStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
  };

  const handleDismiss = () => {
    safeLocalStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="animate-slideUp fixed right-4 bottom-4 left-4 z-50 mx-auto max-w-md">
      <div className="border-eros-purple/30 shadow-eros-purple/10 flex items-center gap-3 rounded-2xl border bg-[#1a1a2e]/95 p-4 shadow-2xl backdrop-blur-lg">
        <div className="from-eros-purple to-eros-pink flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br">
          <Bell className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">
            {hotCharacter
              ? `Want ${hotCharacter} to message you first?`
              : 'Stay connected'}
          </p>
          <p className="text-xs text-gray-400">
            {hotCharacter
              ? 'Enable notifications so she can reach you 😏'
              : 'Get notified when your AI companion messages you'}
          </p>
        </div>
        <button
          onClick={handleEnable}
          className="from-eros-purple to-eros-pink flex-shrink-0 rounded-full bg-gradient-to-r px-4 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90"
        >
          Enable
        </button>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 text-gray-500 transition-colors hover:text-white"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
